import RegistrationConsentBlock, { type RegistrationConsentState } from "@/components/auth/RegistrationConsentBlock";
import { Button } from "@/domains/shared/components/Button";
import { Dialog, DialogBody, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/domains/shared/components/Dialog";
import { authMeQueryKey } from "@/domains/shared/hooks/useAuth";
import type { IUser } from "@/domains/shared/models/IUser";
import { Roles } from "@/domains/shared/models/roles";
import { api, describeApiError } from "@/domains/shared/services/api";
import { queryClient } from "@/domains/shared/services/queryClient";
import { normalizePlanSlug } from "@/domains/subscription/plans";
import { getIsMultiLanguageEnabled, getTranslation, useLocale } from "@mivo/i18n";
import { useState } from "react";
import { toast } from "sonner";

type LegalReacceptModalProps = {
    open: boolean;
    user: IUser;
    onAccepted: () => void;
};

const emptyConsent: RegistrationConsentState = {
    acceptedTerms: false,
    acceptedPrivacy: false,
    acceptedRoleDeclaration: false,
};

const LEGAL_REACCEPT_MODAL_TRANSLATIONS = {
    title: {
        "pt-BR": "Atualização dos termos legais",
        en: "Legal terms update",
        es: "Actualización de los términos legales",
        de: "Aktualisierung der rechtlichen Bestimmungen",
    },
    description: {
        "pt-BR": "Para continuar usando o Mivo, aceite a versão atual dos documentos abaixo.",
        en: "To continue using Mivo, accept the current version of the documents below.",
        es: "Para seguir usando Mivo, acepta la versión actual de los documentos a continuación.",
        de: "Um Mivo weiterhin nutzen zu können, akzeptieren Sie die aktuelle Version der folgenden Dokumente.",
    },
    missingConsent: {
        "pt-BR": "Marque os três aceites para continuar.",
        en: "Select all three acceptances to continue.",
        es: "Marca las tres aceptaciones para continuar.",
        de: "Wählen Sie alle drei Akzeptanzen aus, um fortzufahren.",
    },
    pendingAcceptance: {
        "pt-BR": "Ainda há documentos pendentes de aceite.",
        en: "There are still documents pending acceptance.",
        es: "Todavía hay documentos pendientes de aceptación.",
        de: "Es liegen noch Unterlagen zur Annahme vor.",
    },
    success: {
        "pt-BR": "Aceites registrados. Você já pode continuar.",
        en: "Acceptances recorded. You can continue now.",
        es: "Aceptaciones registradas. Ya puedes continuar.",
        de: "Abnahmen erfasst. Sie können jetzt fortfahren.",
    },
    submit: {
        "pt-BR": "Confirmar aceites",
        en: "Confirm acceptances",
        es: "Confirmar aceptaciones",
        de: "Bestätigen Sie Annahmen",
    },
    saving: {
        "pt-BR": "Salvando…",
        en: "Saving...",
        es: "Guardando...",
        de: "Sparen...",
    },
} as const;

export default function LegalReacceptModal({ open, user, onAccepted }: LegalReacceptModalProps) {
    const [consent, setConsent] = useState<RegistrationConsentState>(emptyConsent);
    const [loading, setLoading] = useState(false);
    const { locale } = useLocale();

    const role = user.role === Roles.Therapist ? Roles.Therapist : Roles.Parent;

    const submit = async () => {
        if (!consent.acceptedTerms || !consent.acceptedPrivacy || !consent.acceptedRoleDeclaration) {
            toast.error(getTranslation(LEGAL_REACCEPT_MODAL_TRANSLATIONS.missingConsent));
            return;
        }

        setLoading(true);
        try {
            const { data } = await api.post<{
                requiresAcceptance: boolean;
                user?: IUser;
            }>("/auth/legal-accept", {
                acceptedTerms: true,
                acceptedPrivacy: true,
                acceptedRoleDeclaration: true,
                acceptedLocale: getIsMultiLanguageEnabled() ? locale : "pt-BR",
            });

            if (data.requiresAcceptance) {
                toast.error(getTranslation(LEGAL_REACCEPT_MODAL_TRANSLATIONS.pendingAcceptance));
                return;
            }

            if (data.user) {
                queryClient.setQueryData(authMeQueryKey, data.user);
            } else {
                await queryClient.invalidateQueries({ queryKey: authMeQueryKey });
            }

            setConsent(emptyConsent);
            onAccepted();
            toast.success(getTranslation(LEGAL_REACCEPT_MODAL_TRANSLATIONS.success));
        } catch (err) {
            toast.error(describeApiError(err));
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={() => {}}>
            <DialogContent className="flex max-h-[min(92vh,960px)] w-[calc(100vw-2rem)] max-w-lg flex-col gap-0 overflow-hidden rounded-3xl p-0 sm:max-w-lg" data-testid="legal-reaccept-modal" dismissable={false}>
                <DialogHeader className="shrink-0 px-6 pt-6 pb-3">
                    <DialogTitle slot="title">{getTranslation(LEGAL_REACCEPT_MODAL_TRANSLATIONS.title)}</DialogTitle>
                    <DialogDescription>{getTranslation(LEGAL_REACCEPT_MODAL_TRANSLATIONS.description)}</DialogDescription>
                </DialogHeader>

                <DialogBody>
                    <RegistrationConsentBlock
                        role={role}
                        consent={consent}
                        onConsentChange={setConsent}
                        testIdPrefix="legal-reaccept"
                        intendedPlanId={role === Roles.Therapist ? normalizePlanSlug(user.subscriptionPlan) : undefined}
                    />
                </DialogBody>

                <DialogFooter className="mx-0 mb-0 gap-2 sm:gap-3">
                    <Button type="button" className="w-full" disabled={loading} onClick={() => void submit()} data-testid="legal-reaccept-submit">
                        {loading
                            ? getTranslation(LEGAL_REACCEPT_MODAL_TRANSLATIONS.saving)
                            : getTranslation(LEGAL_REACCEPT_MODAL_TRANSLATIONS.submit)}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
